import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Zap } from "lucide-react";
import {
  getMyReminders, updateCommTaskStatus, completeCommFollowUp,
  getQuickTaskSummary, updateQuickTaskStatus, getStaff,
} from "../lib/api";
import { useAuth } from "../App";
import { TaskDialog } from "../pages/QuickTasksPage";

function fmtDate(d) {
  if (!d) return "";
  try {
    return new Date(d + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" });
  } catch {
    return d;
  }
}

function daysFromToday(yyyyMmDd) {
  if (!yyyyMmDd) return null;
  const d = new Date(yyyyMmDd + "T00:00:00");
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((d - today) / 86400000);
}

// "3d" / "5h" / "just now" since an ISO timestamp.
function sinceLabel(iso) {
  if (!iso) return "";
  try {
    const then = new Date(iso).getTime();
    const mins = Math.max(0, Math.round((Date.now() - then) / 60000));
    if (mins < 60) return "just now";
    const hrs = Math.round(mins / 60);
    if (hrs < 24) return `${hrs}h`;
    return `${Math.round(hrs / 24)}d`;
  } catch {
    return "";
  }
}

function ItemRow({ item, onDone }) {
  const isFollowUp = item.kind === "follow_up";
  const isTask = item.kind === "task";
  const headline = isFollowUp
    ? (item.subject || item.contact_name || "Follow-up")
    : item.description;
  const context = isFollowUp
    ? (item.description && item.description !== "Follow-up" ? item.description : null)
    : (item.subject || item.contact_name || null);

  return (
    <div className="flex items-start gap-3 py-2 border-b last:border-b-0" data-testid={`my-reminder-${item.kind}-${item.task_id || item.comm_id}`}>
      <input
        type="checkbox"
        className="mt-1"
        checked={false}
        onChange={() => onDone(item)}
        title="Mark done"
        data-testid={`my-reminder-done-${item.task_id || item.comm_id}`}
      />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-gray-900 truncate">{headline}</div>
        <div className="text-xs text-muted-foreground flex items-center gap-2 flex-wrap mt-0.5">
          {isTask && <Badge variant="outline" className="bg-sky-50 text-sky-700 text-[10px] py-0">Task</Badge>}
          {isFollowUp && <Badge variant="outline" className="bg-amber-50 text-amber-700 text-[10px] py-0">Follow-up</Badge>}
          {item.student_name && <span>· {item.student_name}</span>}
          {context && <span className="truncate">· {context}</span>}
        </div>
      </div>
      {item.due_date && <span className="text-xs font-medium text-gray-600 whitespace-nowrap">{fmtDate(item.due_date)}</span>}
    </div>
  );
}

// A single open Quick Task assigned to the current user.
function QuickTaskRow({ task, currentUserId, onToggle }) {
  const diff = daysFromToday(task.due_date);
  const overdue = diff != null && diff < 0;
  const dueToday = diff === 0;
  const fromSomeoneElse = task.created_by && task.created_by !== currentUserId;
  return (
    <div className="flex items-start gap-3 py-2 border-b last:border-b-0" data-testid={`my-quick-task-${task.id}`}>
      <input
        type="checkbox"
        className="mt-1 w-4 h-4 cursor-pointer"
        checked={false}
        onChange={() => onToggle(task)}
        title="Mark done"
        data-testid={`my-quick-task-done-${task.id}`}
      />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-gray-900 truncate">{task.title}</div>
        {fromSomeoneElse && (
          <div className="text-xs text-muted-foreground mt-0.5">from {task.created_by_name || "a colleague"}</div>
        )}
      </div>
      {task.due_date && (
        <span
          className={`text-xs font-medium whitespace-nowrap ${overdue ? "text-red-600" : dueToday ? "text-amber-600" : "text-gray-600"}`}
          data-testid={`my-quick-task-due-${task.id}`}
        >
          {fmtDate(task.due_date)}
        </span>
      )}
    </div>
  );
}

export default function MyTasksWidget() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState({ overdue: [], today: [], upcoming: [], total: 0 });
  const [qt, setQt] = useState({ assigned_to_me: [], assigned_by_me: [], open_count: 0 });
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);

  const load = async () => {
    try {
      const [rem, sum] = await Promise.all([
        getMyReminders().catch(() => ({ data: { overdue: [], today: [], upcoming: [], total: 0 } })),
        getQuickTaskSummary().catch(() => ({ data: { assigned_to_me: [], assigned_by_me: [], open_count: 0 } })),
      ]);
      setData(rem.data);
      setQt(sum.data);
      // Let the sidebar badge refresh immediately (not just on next navigation).
      try { window.dispatchEvent(new Event("quicktasks-changed")); } catch { /* no-op */ }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getStaff().then((r) => setStaff([...(r.data || [])].sort((a, b) => (a.name || "").localeCompare(b.name || "")))).catch(() => {});
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, []);

  const handleDone = async (item) => {
    try {
      if (item.kind === "task") {
        await updateCommTaskStatus(item.comm_id, item.task_id, "done");
      } else {
        await completeCommFollowUp(item.comm_id);
      }
      toast.success("Marked done");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update");
    }
  };

  const toggleQuickTask = async (task) => {
    // optimistic remove from the list
    setQt((prev) => ({
      ...prev,
      assigned_to_me: prev.assigned_to_me.filter((t) => t.id !== task.id),
      open_count: Math.max(0, (prev.open_count || 1) - 1),
    }));
    try {
      await updateQuickTaskStatus(task.id, "done");
      toast.success("Marked done");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update");
      load();
    }
  };

  const myOpen = qt.assigned_to_me || [];
  const byMe = qt.assigned_by_me || [];
  const hasAnything = data.total > 0 || myOpen.length > 0 || byMe.length > 0;

  const HeaderButtons = (
    <div className="flex items-center gap-2">
      <Button
        size="sm"
        variant="outline"
        onClick={() => setDialogOpen(true)}
        data-testid="my-tasks-quick-task-btn"
      >
        <Zap className="w-3.5 h-3.5 mr-1" /> Quick Task
      </Button>
      <Button size="sm" variant="outline" onClick={() => navigate("/communications")} data-testid="my-tasks-open-page">
        {data.total > 0 ? "See all" : "Open"}
      </Button>
    </div>
  );

  const Dialog = (
    <TaskDialog
      open={dialogOpen}
      onOpenChange={setDialogOpen}
      editing={null}
      staff={staff}
      currentUserId={user?.id}
      onSaved={load}
    />
  );

  if (loading) return null;

  if (!hasAnything) {
    return (
      <Card data-testid="my-tasks-widget-empty">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base">My Tasks & Reminders</CardTitle>
              <CardDescription>You're all caught up. 🎉</CardDescription>
            </div>
            {HeaderButtons}
          </div>
        </CardHeader>
        {Dialog}
      </Card>
    );
  }

  return (
    <Card className="animate-fade-in" data-testid="my-tasks-widget">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              My Tasks & Reminders
              {data.total > 0 && <Badge className="bg-[#00305E] text-white">{data.total}</Badge>}
            </CardTitle>
            <CardDescription>Click the checkbox to mark something done.</CardDescription>
          </div>
          {HeaderButtons}
        </div>
      </CardHeader>
      <CardContent className="space-y-4 pt-0">
        {/* --- Quick Tasks assigned to me (open) --- */}
        {myOpen.length > 0 && (
          <div data-testid="my-quick-tasks-section">
            <div className="text-xs font-semibold uppercase tracking-wide text-emerald-700 mb-1 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block" />
              My open tasks ({qt.open_count})
            </div>
            <div className="bg-emerald-50/50 rounded-md px-3 border border-emerald-100">
              {myOpen.slice(0, 5).map((t) => (
                <QuickTaskRow key={t.id} task={t} currentUserId={user?.id} onToggle={toggleQuickTask} />
              ))}
            </div>
            {myOpen.length > 5 && (
              <button
                className="text-xs text-[#00305E] font-medium mt-1 hover:underline"
                onClick={() => navigate("/quick-tasks")}
                data-testid="my-quick-tasks-view-all"
              >
                View all ({qt.open_count})
              </button>
            )}
          </div>
        )}

        {/* --- Tasks I assigned to others, still open --- */}
        {byMe.length > 0 && (
          <div data-testid="my-assigned-by-me-section">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-600 mb-1 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-slate-400 inline-block" />
              Assigned by me, still open ({byMe.length})
            </div>
            <div className="bg-slate-50 rounded-md px-3 border border-slate-200">
              {byMe.slice(0, 3).map((t) => (
                <div key={t.id} className="flex items-start gap-3 py-2 border-b last:border-b-0" data-testid={`assigned-by-me-${t.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-900 truncate">{t.title}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {t.assignee_name || "Unassigned"} · open {sinceLabel(t.created_at)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {byMe.length > 3 && (
              <button
                className="text-xs text-[#00305E] font-medium mt-1 hover:underline"
                onClick={() => navigate("/quick-tasks")}
                data-testid="my-assigned-by-me-view-all"
              >
                View all ({byMe.length})
              </button>
            )}
          </div>
        )}

        {/* --- Existing communications-log reminders (unchanged) --- */}
        {data.overdue.length > 0 && (
          <div data-testid="my-tasks-overdue-section">
            <div className="text-xs font-semibold uppercase tracking-wide text-red-700 mb-1 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-red-600 inline-block" />
              Overdue ({data.overdue.length})
            </div>
            <div className="bg-red-50 rounded-md px-3 border border-red-200">
              {data.overdue.map((it) => <ItemRow key={`o-${it.task_id || it.comm_id}`} item={it} onDone={handleDone} />)}
            </div>
          </div>
        )}
        {data.today.length > 0 && (
          <div data-testid="my-tasks-today-section">
            <div className="text-xs font-semibold uppercase tracking-wide text-orange-700 mb-1 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-orange-500 inline-block" />
              Due Today ({data.today.length})
            </div>
            <div className="bg-orange-50 rounded-md px-3 border border-orange-200">
              {data.today.map((it) => <ItemRow key={`t-${it.task_id || it.comm_id}`} item={it} onDone={handleDone} />)}
            </div>
          </div>
        )}
        {data.upcoming.length > 0 && (
          <div data-testid="my-tasks-upcoming-section">
            <div className="text-xs font-semibold uppercase tracking-wide text-blue-700 mb-1 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-blue-500 inline-block" />
              Upcoming (next 14 days, {data.upcoming.length})
            </div>
            <div className="bg-blue-50/40 rounded-md px-3 border border-blue-100">
              {data.upcoming.map((it) => <ItemRow key={`u-${it.task_id || it.comm_id}`} item={it} onDone={handleDone} />)}
            </div>
          </div>
        )}
      </CardContent>
      {Dialog}
    </Card>
  );
}
